// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ShareOptions } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setManageShareListModalIsOpen from "@insite/client-framework/Store/Components/ManageShareListModal/Handlers/SetManageShareListModalIsOpen";
import setShareListDetailModalIsOpen from "@insite/client-framework/Store/Components/ShareListDetailModal/Handlers/SetShareListDetailModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import HelpCircle from "@insite/mobius/Icons/HelpCircle";
import Users from "@insite/mobius/Icons/Users";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ListItem, { UnorderedList } from "@insite/mobius/Lists";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Lock from "@werner/XDL/Icons/Lock";
import { createLinkIconStyles } from "@werner/XDL/Utilities";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: WishListSharingStatusStyles;
    /**
     * @deprecated Use the `wishList` property instead.
     */
    isSharedList?: boolean;
    /**
     * @deprecated Use the `wishList` property instead.
     */
    wishListSharesCount?: number;
    /**
     * @deprecated Use the `wishList` property instead.
     */
    sharedByDisplayName?: string;
    wishList?: WishListModel;
    showNoPermissionsTooltip?: boolean;
}

const mapDispatchToProps = {
    setManageShareListModalIsOpen,
    setShareListDetailModalIsOpen,
};

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
});

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface WishListSharingStatusStyles {
    statusText?: LinkPresentationProps;
    privateIcon?: IconPresentationProps;
    sharedIcon?: IconPresentationProps;
    privateTooltip?: TooltipPresentationProps;
    autogeneratedIcon?: IconPresentationProps;
    autogeneratedTooltip?: TooltipPresentationProps;
    // XNG-Changes
    privateLink?: LinkPresentationProps;
    shareDetailsModalContent?: InjectableCss;
    shareDetailsModalheadings?: TypographyProps;
}

export const wishListSharingStatusStyles: WishListSharingStatusStyles = {
    privateIcon: {
        src: HelpCircle,
        size: 15,
        css: css`
            margin-left: 5px;
        `,
    },
    sharedIcon: {
        src: Users,
        size: 15,
        css: css`
            margin-right: 5px;
        `,
    },
    privateLink: {
        color: "primary",
        icon: {
            iconProps: {
                src: Lock,
                size: 16,
                css: css`
                    margin-right: 4px;
                `,
            },
            position: "left",
        },
    },
    statusText: {
        color: "primary",
        icon: {
            iconProps: {
                src: Users,
                size: 16,
                css: css`
                    margin-right: 4px;
                `,
            },
            position: "left",
        },
    },
    shareDetailsModalContent: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 12px;
            & > .shared-users-section {
                display: flex;
                flex-direction: column;
            }
        `,
    },
    shareDetailsModalheadings: {
        weight: 500,
        css: css`
            display: flex;
            & > .owner-name-heading:after {
                content: ":";
                margin-right: 4px;
            }
            & > .owner-name {
                font-weight: 400;
            }
        `,
    },
    autogeneratedIcon: {
        src: Lock,
        size: 15,
        css: css`
            margin-right: 5px;
        `,
    },
};

const WishListSharingStatus: FC<Props> = ({
    extendedStyles,
    isSharedList,
    wishListSharesCount,
    sharedByDisplayName,
    wishList,
    setManageShareListModalIsOpen,
    setShareListDetailModalIsOpen,
    wishListSettings,
    showNoPermissionsTooltip,
}) => {
    const [styles] = React.useState(() => mergeToNew(wishListSharingStatusStyles, extendedStyles));
    // <XNG-Changes>
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const handleCloseModal = () => {
        setIsModalOpen(false);
    };
    const handleOpenModal = () => {
        setIsModalOpen(true);
    };
    // </XNG-Changes>
    if (!wishList || !wishListSettings?.allowMultipleWishLists || !wishListSettings?.allowListSharing) {
        return null;
    }

    const manageShareListClickHandler = () => {
        setManageShareListModalIsOpen({ modalIsOpen: true, wishListId: wishList.id });
    };

    const shareListDetailClickHandler = () => {
        setShareListDetailModalIsOpen({ modalIsOpen: true, listOwner: wishList?.sharedByDisplayName });
    };

    const isSharedListInner = isSharedList || wishList.isSharedList || false;
    const wishListSharesCountInner = wishListSharesCount || wishList.wishListSharesCount || 0;
    const sharedByDisplayNameInner = sharedByDisplayName || wishList.sharedByDisplayName;
    // <XNG-Changes>
    const isSharedWithAllCustomers = wishList.isSharedList && wishList.shareOption === ShareOptions.AllCustomerUsers;
    // </XNG-Changes>

    if (wishList.isAutogenerated) {
        return (
            <Typography {...styles.statusText} data-test-selector="myLists_autogenerated">
                <Icon {...styles.autogeneratedIcon} />
                {translate("Auto-generated")}
                <Tooltip {...styles.privateTooltip} text={siteMessage("Lists_Autogenerated_List_Tooltip") as string} />
            </Typography>
        );
    }

    return (
        <>
            {!isSharedListInner && wishList.shareOption !== "Private" && (
                <Tooltip
                    {...styles.privateTooltip}
                    triggerComponent={
                        <Link {...styles.privateLink} data-test-selector="myLists_PrivateLabel" aria-label="Private">
                            {translate("Private")}
                        </Link>
                    }
                    text={siteMessage("Lists_Share_List_Tooltip") as string}
                />
            )}
            {!isSharedListInner && wishList.shareOption !== "Private" && (
                <>
                    <Link
                        typographyProps={{ ...styles.statusText }}
                        onClick={() => manageShareListClickHandler()}
                        data-test-selector="shareWithOthers"
                        aria-label={`${translate("Shared with")} ${wishListSharesCountInner} ${translate("others")}`}
                    >
                        {/* <Icon {...styles.sharedIcon} /> */}
                        {translate("Shared with")} {wishListSharesCountInner} {translate("others")}
                    </Link>
                </>
            )}
            {isSharedListInner && sharedByDisplayNameInner && (
                <>
                    {!(!wishList?.allowEdit && showNoPermissionsTooltip) && (
                        <Link
                            typographyProps={{ ...styles.statusText }}
                            onClick={() => shareListDetailClickHandler()}
                            data-test-selector="myLists_listSharedBy"
                            aria-label={`${translate("Shared By")} ${sharedByDisplayNameInner}`}
                        >
                            {/* <Icon {...styles.sharedIcon} /> */}
                            {translate("Shared By")} {sharedByDisplayNameInner}
                        </Link>
                    )}
                    {!wishList?.allowEdit && showNoPermissionsTooltip && (
                        <Tooltip
                            {...styles.privateTooltip}
                            text={siteMessage("Lists_Share_List_No_Permissions_Tooltip") as string}
                            triggerComponent={
                                <Link
                                    onClick={handleOpenModal}
                                    {...styles.statusText}
                                    data-test-selector="myLists_listSharedBy"
                                    aria-label={`${translate("Shared By")} ${sharedByDisplayNameInner}`}
                                >
                                    {translate("Shared By")} {sharedByDisplayNameInner}
                                </Link>
                            }
                        />
                    )}
                    <TwoButtonModal
                        headlineText={translate("Share Details")}
                        modalIsOpen={isModalOpen}
                        messageText={
                            <StyledWrapper {...styles.shareDetailsModalContent}>
                                <Typography as="p" {...styles.shareDetailsModalheadings}>
                                    <Typography as="p" className="owner-name-heading">
                                        {translate("List Owner")}
                                    </Typography>
                                    <Typography as="p" className="owner-name">
                                        {sharedByDisplayNameInner}
                                    </Typography>
                                </Typography>
                                {isSharedWithAllCustomers && (
                                    <Typography>{translate("Shared with all users on billing account")}</Typography>
                                )}
                                {!!wishList?.sharedUsers?.length && wishList?.sharedUsers?.length > 0 && (
                                    <StyledWrapper className="shared-users-section">
                                        <Typography {...styles.shareDetailsModalheadings}>
                                            {translate("Shared With")}
                                        </Typography>
                                        <UnorderedList>
                                            {wishList?.sharedUsers?.map(user => (
                                                <ListItem key={user.id}>{user.displayName}</ListItem>
                                            ))}
                                        </UnorderedList>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        }
                        cancelButtonText={translate("Cancel")}
                        submitButtonText={translate("Ok")}
                        onCancel={handleCloseModal}
                        onSubmit={handleCloseModal}
                        extendedStyles={{
                            modal: {
                                sizeVariant: "medium",
                            },
                        }}
                    ></TwoButtonModal>
                </>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(WishListSharingStatus);
